import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { CircleAlert, Download, LogIn, PlusCircle, SquarePen, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { httpClient } from '../../common/httpclient';
import StatusChip from '../../component/common/StatusChip';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import MobileSheet from '../../component/common/MobileSheet';
import { KitCard } from '../../component/common/MobileKit';
import { KitBanner, KitKV } from '../../component/common/MobileKitExtras';
import { classifyAuditAction, formatDateTimeUTC } from './auditUtils';

// Drawer de detalhe do log de auditoria — DESIGN-SYSTEM.md §5.6 (drawer
// lateral, 560px), mesmo padrão de `pages/categories/List.js`/
// `pages/transactions/List.js` (drawer de visualização somente leitura).
// Renomeado de "Modal de detalhe de auditoria" (decisão do FRONTEND-TASKS.md
// de usar drawer lateral em vez de modal central) — arquivo antigo
// `AuditDetailModal.js` removido.
//
// Aba "Detalhes": metadados de `GET /audit/diff/{id}`.details (`user_name`,
// `action`, `resource`, `created_at`). "Endereço IP"/"Dispositivo" não têm
// dado real disponível nesse endpoint hoje — ver nota de gap de backend em
// `pages/audit/List.js` — e são exibidos como "Não disponível".
//
// Aba "Alterações": diff campo-a-campo de `.diff.before_data`/`after_data`,
// mostrando só os campos que de fato mudaram (nunca todos os campos do
// registro). O rótulo/conteúdo da aba se adapta ao formato real dos dados
// (não à ação): se só existe `after_data`, é uma criação ("Valores
// criados"); se só existe `before_data`, é uma remoção ("Dados removidos");
// se os dois existem, é um diff Antes/Depois de verdade ("Alterações"). Se
// nenhum dos dois tem conteúdo (ex.: `serviceorder/payer.go` grava
// Before/AfterData nulos), a aba nem aparece.
export default function AuditDetailDrawer({ open, onClose, auditId, row }) {
    const theme = useTheme();
    const isMobileShell = useIsMobileShell();
    const [loading, setLoading] = useState(false);
    const [diffData, setDiffData] = useState(null);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState('detalhes');

    useEffect(() => {
        if (open && auditId) {
            fetchDiffData(auditId);
            setTab('detalhes');
        } else {
            setDiffData(null);
            setError(null);
            setTab('detalhes');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, auditId]);

    const fetchDiffData = async (id) => {
        setLoading(true);
        setError(null);
        try {
            const response = await httpClient.get(`/audit/diff/${id}`);
            if (response.status === 200 && response.data) {
                setDiffData(response.data);
            } else {
                setError('Erro ao carregar detalhes da atividade');
            }
        } catch (err) {
            console.error('Error fetching audit diff:', err);
            setError('Erro ao carregar detalhes da atividade');
        } finally {
            setLoading(false);
        }
    };

    const details = diffData?.details || row || null;
    const beforeData = diffData?.diff?.before_data || {};
    const afterData = diffData?.diff?.after_data || {};

    const beforeFields = Object.keys(beforeData).filter((k) => hasValue(beforeData[k])).sort();
    const afterFields = Object.keys(afterData).filter((k) => hasValue(afterData[k])).sort();
    const hasBefore = beforeFields.length > 0;
    const hasAfter = afterFields.length > 0;

    const allFields = Array.from(new Set([...beforeFields, ...afterFields])).sort();
    const changedFields = allFields.filter((key) => isFieldChanged(beforeData, afterData, key));

    // 'both' | 'created' | 'deleted' | null (sem dado nenhum -> aba some)
    const mode = hasBefore && hasAfter ? 'both' : hasAfter ? 'created' : hasBefore ? 'deleted' : null;

    const bucket = classifyAuditAction(details?.action);
    const bucketMeta = BUCKET_META[bucket];
    const { datePart, timePart } = formatDateTimeUTC(details?.created_at);

    const changesTabLabel = mode === 'created' ? 'Valores criados' : mode === 'deleted' ? 'Dados removidos' : 'Alterações';
    const changesNote = mode === 'created'
        ? 'Registro novo — não existiam valores anteriores. Os campos definidos na criação estão listados abaixo.'
        : mode === 'deleted'
            ? 'Registro excluído — valores existentes até o momento da exclusão.'
            : 'Somente os campos alterados são exibidos.';

    // Shell mobile: folha de baixo `#det` de `sistema/auditoria.html` (Detalhe do evento).
    if (isMobileShell) {
        const changeRows = mode === 'both'
            ? changedFields.map((key) => (
                <Box key={key} sx={{ fontSize: 12.5, mt: '8px', '&:first-of-type': { mt: 0 } }}>
                    <Box component="span" sx={{ color: 'text.disabled' }}>{getFieldLabel(key)}:</Box>{' '}
                    <s>{formatValue(beforeData[key])}</s> → <b>{formatValue(afterData[key])}</b>
                </Box>
            ))
            : (mode === 'created' ? afterFields : beforeFields).map((key) => (
                <Box key={key} sx={{ fontSize: 12.5, mt: '8px', '&:first-of-type': { mt: 0 } }}>
                    <Box component="span" sx={{ color: 'text.disabled' }}>{getFieldLabel(key)}:</Box>{' '}
                    {mode === 'created' ? <b>{formatValue(afterData[key])}</b> : <s>{formatValue(beforeData[key])}</s>}
                </Box>
            ));
        return (
            <MobileSheet open={open} onClose={onClose} title="Detalhe do evento">
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
                )}
                {!loading && error && (
                    <KitBanner tone="danger">{error}</KitBanner>
                )}
                {!loading && !error && (
                    <>
                        <KitKV label="Ação" value={details?.action || '—'} />
                        <KitKV label="Usuário" value={details?.user_name || '—'} />
                        <KitKV label="Recurso" value={details?.resource || '—'} />
                        <KitKV label="Data" value={details?.created_at ? `${datePart} · ${timePart}` : '—'} />
                        <KitKV label="IP" value="Não disponível" />
                        <KitKV label="Dispositivo" value="Não disponível" />
                        {mode && (
                            <>
                                <Box sx={{ mt: '12px', fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.disabled', lineHeight: 1.4 }}>
                                    {changesTabLabel}
                                </Box>
                                <KitCard sx={{ boxShadow: 'none', mt: '8px', wordBreak: 'break-word' }}>
                                    {changeRows.length > 0 ? changeRows : (
                                        <Box sx={{ fontSize: 12.5, color: 'text.disabled' }}>Nenhum campo alterado foi registrado para esta atividade.</Box>
                                    )}
                                </KitCard>
                            </>
                        )}
                    </>
                )}
            </MobileSheet>
        );
    }

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' } } }}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 560 }, maxWidth: '100vw',
                    borderRadius: 0,
                    boxShadow: theme.palette.mode === 'dark' ? '-8px 0 32px rgba(0,0,0,0.4)' : '-8px 0 32px rgba(0,0,0,0.12)',
                    borderLeft: '1px solid', borderColor: 'divider',
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                },
            }}
        >
            {open && (
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {/* drawer-head */}
                    <Box sx={{ p: '22px 22px 16px', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.25, flexShrink: 0 }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Box sx={{
                                width: 38, height: 38, borderRadius: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                bgcolor: bucketMeta.bg, color: bucketMeta.fg, mb: '10px',
                            }}>
                                <bucketMeta.Icon size={18} strokeWidth={2} />
                            </Box>
                            <Typography sx={{ fontSize: '17px', fontWeight: 800, mb: '4px' }}>
                                {bucketMeta.label}{details?.resource ? ` · ${details.resource}` : ''}
                            </Typography>
                            <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={details?.action}>
                                {details?.action || '—'}
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose} size="small" aria-label="Fechar"><X size={15} strokeWidth={2} /></IconButton>
                    </Box>

                    {/* drawer-body */}
                    <Box sx={{ p: '20px 22px', flex: 1, overflowY: 'auto' }}>
                        {loading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                                <CircularProgress size={30} />
                            </Box>
                        )}

                        {!loading && error && (
                            <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'error.bg', borderRadius: 2, border: '1px solid', borderColor: 'error.main' }}>
                                <Typography variant="body2" sx={{ color: 'error.main' }}>{error}</Typography>
                            </Box>
                        )}

                        {!loading && !error && (
                            <>
                                {/* seg-tabs — só mostra "Alterações" quando há diff aplicável */}
                                {mode && (
                                    <ToggleButtonGroup
                                        exclusive
                                        value={tab}
                                        onChange={(e, v) => v && setTab(v)}
                                        sx={{ width: 'fit-content', p: '4px', gap: '4px', borderRadius: '11px', mb: '16px' }}
                                    >
                                        <ToggleButton value="detalhes" sx={segTabButtonSx}>Detalhes</ToggleButton>
                                        <ToggleButton value="alteracoes" sx={segTabButtonSx}>{changesTabLabel}</ToggleButton>
                                    </ToggleButtonGroup>
                                )}

                                {tab === 'detalhes' && (
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '14px 18px' }}>
                                        <DetailItem label="Usuário" value={details?.user_name || '—'} />
                                        <DetailItem
                                            label="Ação"
                                            value={<StatusChip domain="auditAction" value={bucket} label={details?.action || '—'} />}
                                        />
                                        <DetailItem label="Recurso" value={details?.resource || '—'} />
                                        <DetailItem label="Data/Hora" value={details?.created_at ? `${datePart} às ${timePart}` : '—'} />
                                        <DetailItem label="Endereço IP" value="Não disponível" muted />
                                        <DetailItem label="Dispositivo" value="Não disponível" muted />
                                    </Box>
                                )}

                                {tab === 'alteracoes' && mode && (
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', mb: '12px', color: 'text.disabled' }}>
                                            <CircleAlert size={13} strokeWidth={2} />
                                            <Typography sx={{ fontSize: '11.5px' }}>{changesNote}</Typography>
                                        </Box>

                                        {mode === 'both' && (
                                            changedFields.length > 0 ? (
                                                <DiffTable
                                                    rows={changedFields.map((key) => ({
                                                        key,
                                                        label: getFieldLabel(key),
                                                        before: formatValue(beforeData[key]),
                                                        after: formatValue(afterData[key]),
                                                    }))}
                                                />
                                            ) : (
                                                <EmptyChangesNote text="Nenhum campo alterado foi registrado para esta atividade." />
                                            )
                                        )}

                                        {mode === 'created' && (
                                            <SingleColumnDiffTable
                                                columnLabel="Valor definido"
                                                tone="after"
                                                rows={afterFields.map((key) => ({ key, label: getFieldLabel(key), value: formatValue(afterData[key]) }))}
                                            />
                                        )}

                                        {mode === 'deleted' && (
                                            <SingleColumnDiffTable
                                                columnLabel="Valor removido"
                                                tone="before"
                                                rows={beforeFields.map((key) => ({ key, label: getFieldLabel(key), value: formatValue(beforeData[key]) }))}
                                            />
                                        )}
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>

                    {/* drawer-foot */}
                    <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1.25, justifyContent: 'flex-end', flexShrink: 0 }}>
                        <Button variant="outlined" onClick={onClose}>Fechar</Button>
                    </Box>
                </Box>
            )}
        </Drawer>
    );
}

// Mesmas medidas/ajustes de `ToggleButtonGroup` restilizado já documentados
// em `pages/reports/SalesReport.js` (`segTabButtonSx`) — trilha 4px/11px,
// pill 9px×18px/8px/12.5px-700, sobrescrita por instância porque o tema
// global usa uma trilha mais larga (2px/10px).
const segTabButtonSx = {
    gap: '7px',
    padding: '9px 18px',
    lineHeight: 'normal',
    borderRadius: '8px !important',
    fontSize: '12.5px',
    fontWeight: 700,
};

function DetailItem({ label, value, muted = false }) {
    return (
        <Box>
            <Typography sx={{ fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled', mb: '4px' }}>
                {label}
            </Typography>
            {typeof value === 'string' ? (
                <Typography sx={{ fontSize: '13.5px', fontWeight: muted ? 500 : 600, color: muted ? 'text.disabled' : 'text.primary', lineHeight: 1.4, fontStyle: muted ? 'italic' : 'normal' }}>
                    {value}
                </Typography>
            ) : (
                <Box sx={{ mt: '2px' }}>{value}</Box>
            )}
        </Box>
    );
}

function EmptyChangesNote({ text }) {
    return (
        <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'background.subtle', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{text}</Typography>
        </Box>
    );
}

const diffHeadCellSx = {
    fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled',
    fontWeight: 800, borderBottom: '1px solid', borderColor: 'divider', px: '10px', pb: '8px', pt: 0,
};
const diffBodyCellSx = { px: '10px', py: '10px', borderBottom: '1px solid', borderColor: 'divider', verticalAlign: 'top' };

function DiffPill({ tone, children }) {
    return (
        <Box component="span" sx={{
            display: 'inline-block', px: '10px', py: '4px', borderRadius: '8px', fontSize: '12px', fontWeight: 600, lineHeight: 1.4, wordBreak: 'break-word',
            bgcolor: tone === 'before' ? 'warning.bg' : 'success.bg',
            color: tone === 'before' ? 'warning.main' : 'success.main',
            textDecoration: tone === 'before' ? 'line-through' : 'none',
        }}>
            {children}
        </Box>
    );
}

// Tabela de diff campo-a-campo — DESIGN-SYSTEM.md, extensão introduzida por
// esta tela (`prototipo-log-auditoria.html` `.diff-table`/`.diff-val`):
// "Antes" em tom warning com tachado, "Depois" em tom success, só campos
// alterados.
function DiffTable({ rows }) {
    return (
        <Table size="small" sx={{ width: '100%' }}>
            <TableHead>
                <TableRow>
                    <TableCell sx={diffHeadCellSx}>Campo</TableCell>
                    <TableCell sx={diffHeadCellSx}>Antes</TableCell>
                    <TableCell sx={diffHeadCellSx}>Depois</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((row) => (
                    <TableRow key={row.key}>
                        <TableCell sx={{ ...diffBodyCellSx, fontWeight: 700, color: 'text.primary', whiteSpace: 'nowrap', pl: 0 }}>{row.label}</TableCell>
                        <TableCell sx={diffBodyCellSx}><DiffPill tone="before">{row.before}</DiffPill></TableCell>
                        <TableCell sx={{ ...diffBodyCellSx, pr: 0 }}><DiffPill tone="after">{row.after}</DiffPill></TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

function SingleColumnDiffTable({ rows, columnLabel, tone }) {
    return (
        <Table size="small" sx={{ width: '100%' }}>
            <TableHead>
                <TableRow>
                    <TableCell sx={diffHeadCellSx}>Campo</TableCell>
                    <TableCell sx={diffHeadCellSx}>{columnLabel}</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((row) => (
                    <TableRow key={row.key}>
                        <TableCell sx={{ ...diffBodyCellSx, fontWeight: 700, color: 'text.primary', whiteSpace: 'nowrap', pl: 0 }}>{row.label}</TableCell>
                        <TableCell sx={{ ...diffBodyCellSx, pr: 0 }}><DiffPill tone={tone}>{row.value}</DiffPill></TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

const BUCKET_META = {
    criou: { label: 'Criou', Icon: PlusCircle, bg: 'success.bg', fg: 'success.main' },
    editou: { label: 'Editou', Icon: SquarePen, bg: 'warning.bg', fg: 'warning.main' },
    excluiu: { label: 'Excluiu', Icon: Trash2, bg: 'error.bg', fg: 'error.main' },
    login: { label: 'Login', Icon: LogIn, bg: 'background.subtle', fg: 'text.secondary' },
    outro: { label: 'Outro', Icon: Download, bg: 'background.subtle', fg: 'text.secondary' },
};

function hasValue(value) {
    return value !== null && value !== undefined && value !== '';
}

// Mesma comparação "antes !== depois" já usada pela implementação anterior
// (`AuditDetailModal.js`): null/undefined/'' de um dos lados contam como
// "sem valor" (equivalentes entre si), não como uma string vazia diferente
// de `null`.
function isFieldChanged(beforeData, afterData, key) {
    const normalize = (v) => (v === null || v === undefined || v === '' ? null : v);
    const before = normalize(beforeData[key]);
    const after = normalize(afterData[key]);
    if (typeof before === 'object' || typeof after === 'object') {
        return JSON.stringify(before) !== JSON.stringify(after);
    }
    return before !== after;
}

function formatValue(value) {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

// Dicionário de rótulos de campo — herdado de `AuditDetailModal.js` (chaves
// da entidade `Produto`, `fv-api/product/product.go` `Product` struct) e
// ampliado com as demais chaves reais gravadas em `BeforeData`/`AfterData`
// por outros domínios (`grep 'Action:\s*"' -A alguns fv-api` — venda, ordem
// de serviço, serviço, máquina de cartão, login, estoque). Chave não mapeada
// cai num fallback que só troca `_` por espaço e capitaliza — nunca inventa
// um rótulo que não seja derivado do nome real do campo.
const FIELD_LABELS = {
    id: 'ID',
    code: 'Código',
    cost: 'Custo',
    name: 'Nome',
    nome: 'Nome',
    image: 'Imagem',
    category: 'Categoria',
    quantity: 'Quantidade',
    supplier: 'Fornecedor',
    is_generic: 'É Genérico',
    generico: 'É Genérico',
    externo: 'Atende Externo',
    preco_terceirizado: 'Preço Terceirizado',
    sale_price: 'Preço de Venda',
    preco: 'Preço',
    description: 'Descrição',
    descricao: 'Descrição',
    subcategory: 'Subcategoria',
    min_quantity: 'Quantidade Mínima',
    category_name: 'Nome da Categoria',
    supplier_name: 'Nome do Fornecedor',
    subcategory_name: 'Nome da Subcategoria',
    quantity_sold: 'Quantidade Vendida',
    profit_margin: 'Margem de Lucro',
    expected_profit_margin: 'Margem de Lucro Esperada',
    quantidade_atual: 'Quantidade Atual',
    numero_da_venda: 'Número da Venda',
    total_da_venda: 'Total da Venda',
    numero_da_ordem: 'Número da Ordem de Serviço',
    total_da_ordem: 'Total da Ordem de Serviço',
    cliente: 'Cliente',
    veiculo: 'Veículo',
    tax_debit: 'Taxa Débito',
    tax_credit: 'Taxa Crédito',
    tax_pix_qrcode: 'Taxa Pix (QR Code)',
    max_amount_installment: 'Máx. de Parcelas',
    taxas_credito_parcelado: 'Taxas de Crédito Parcelado',
    username: 'Usuário',
    IP_Address: 'Endereço IP',
    User_Agent: 'Navegador / Dispositivo',
};

function getFieldLabel(key) {
    return FIELD_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}
